(function () {
  if (!window.CfonElements || !window.subscribe || !window.publish) return;

  class CfonCollectionSort extends CfonElements.Component {
    onConnect() {
      this.select = this.querySelector('[data-sort-select]');
      this.sectionId = this.dataset.sectionId;
      this.onChange = this.onChange.bind(this);
      if (this.select) this.select.addEventListener('change', this.onChange);
    }

    onDisconnect() {
      if (this.select) this.select.removeEventListener('change', this.onChange);
    }

    onChange() {
      if (!this.select) return;
      publish(PUB_SUB_EVENTS.collectionSortChange, {
        sortBy: this.select.value,
        sectionId: this.sectionId,
      });
    }
  }

  class CfonProductGrid extends CfonElements.Component {
    onConnect() {
      this.sectionId = this.dataset.sectionId;
      this.blockId = this.dataset.blockId;
      this.paginationType = this.dataset.paginationType || 'pagination';
      this.loading = false;
      this.observer = null;

      this.onSortChange = this.onSortChange.bind(this);
      this.onPageChange = this.onPageChange.bind(this);
      this.onPopState = this.onPopState.bind(this);
      this.onPaginationClick = this.onPaginationClick.bind(this);

      this.unsubscribeSort = subscribe(PUB_SUB_EVENTS.collectionSortChange, this.onSortChange);
      this.unsubscribePage = subscribe(PUB_SUB_EVENTS.collectionPageChange, this.onPageChange);
      window.addEventListener('popstate', this.onPopState);

      this.bindPagination();
      this.setupInfinite();
    }

    onDisconnect() {
      if (this.unsubscribeSort) this.unsubscribeSort();
      if (this.unsubscribePage) this.unsubscribePage();
      window.removeEventListener('popstate', this.onPopState);
      this.teardownInfinite();
      this.unbindPagination();
    }

    bindPagination() {
      this.pagination = this.querySelector('[data-pagination]');
      if (!this.pagination) return;
      this.pagination.addEventListener('click', this.onPaginationClick);
    }

    unbindPagination() {
      if (!this.pagination) return;
      this.pagination.removeEventListener('click', this.onPaginationClick);
    }

    onPaginationClick(event) {
      const link = event.target.closest('[data-page]');
      if (!link || !this.pagination.contains(link)) return;
      if (link.getAttribute('aria-current') === 'page') return;
      event.preventDefault();
      const page = Number(link.dataset.page);
      if (!Number.isFinite(page) || page < 1) return;
      publish(PUB_SUB_EVENTS.collectionPageChange, {
        page,
        sectionId: this.sectionId,
        mode: link.dataset.mode || 'replace',
      });
    }

    setupInfinite() {
      this.teardownInfinite();
      if (this.paginationType !== 'infinite') return;
      const sentinel = this.querySelector('[data-sentinel]');
      if (!sentinel) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry || !entry.isIntersecting || this.loading) return;
          const nextPage = Number(sentinel.dataset.nextPage);
          if (!Number.isFinite(nextPage) || nextPage < 2) return;
          this.render({ page: nextPage, mode: 'append' });
        },
        { rootMargin: '200px 0px' }
      );
      this.observer.observe(sentinel);
    }

    teardownInfinite() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }

    onSortChange(event) {
      if (event.sectionId && event.sectionId !== this.sectionId) return;
      this.render({
        sortBy: event.sortBy,
        page: 1,
        mode: 'replace',
      });
    }

    onPageChange(event) {
      if (event.sectionId && event.sectionId !== this.sectionId) return;
      const mode = event.mode || (this.paginationType === 'infinite' ? 'append' : 'replace');
      this.render({
        page: event.page,
        mode,
      });
    }

    onPopState() {
      this.render({
        mode: 'replace',
        updateUrl: false,
      });
    }

    currentParams() {
      return new URLSearchParams(window.location.search);
    }

    startCollectionUpdate(params, page) {
      const { CollectionUpdateEvent } = window.StandardEvents || {};
      if (!CollectionUpdateEvent) return null;
      if (!this.dataset.collectionHandle && this.dataset.collectionId == null) return null;

      const deferred = CollectionUpdateEvent.createPromise();
      const payload = {
        collection: {
          id: this.dataset.collectionId || null,
          handle: this.dataset.collectionHandle || '',
          productsCount: Number(this.dataset.productsCount) || 0,
        },
        productFilters: CollectionUpdateEvent.parseProductFilters(params),
        sortKey: CollectionUpdateEvent.getSortKey(params),
        promise: deferred.promise,
      };
      if (page != null) payload.detail = { page };

      this.dispatchEvent(new CollectionUpdateEvent(payload));
      return deferred;
    }

    async render({ sortBy, page, mode, updateUrl } = {}) {
      if (this.loading) return;
      this.setLoading(true);

      const params = this.currentParams();
      if (sortBy != null) params.set('sort_by', sortBy);
      if (page != null) params.set('page', String(page));
      if (mode === 'replace' && sortBy != null) params.set('page', '1');

      const requestParams = new URLSearchParams(params);
      requestParams.set('section_id', this.sectionId);

      const requestUrl = `${window.location.pathname}?${requestParams.toString()}`;
      const updateEvent = this.startCollectionUpdate(params, page);

      try {
        const tagName = this.tagName.toLowerCase();
        const selector = `${tagName}[data-block-id="${this.blockId}"]`;
        let nextGrid = null;

        const response = await fetch(requestUrl);
        if (response.ok) {
          const text = await response.text();
          if (text && text.trim()) {
            const doc = new DOMParser().parseFromString(text, 'text/html');
            nextGrid = doc.querySelector(selector);
          }
        }

        if (!nextGrid) {
          const fallbackParams = new URLSearchParams(params);
          fallbackParams.delete('section_id');
          const fallbackQuery = fallbackParams.toString();
          const fallbackUrl = fallbackQuery
            ? `${window.location.pathname}?${fallbackQuery}`
            : window.location.pathname;
          const fallbackResponse = await fetch(fallbackUrl);
          if (!fallbackResponse.ok) throw new Error(`Collection render failed: ${fallbackResponse.status}`);
          const fallbackText = await fallbackResponse.text();
          const fallbackDoc = new DOMParser().parseFromString(fallbackText, 'text/html');
          nextGrid = fallbackDoc.querySelector(selector);
        }

        if (!nextGrid) throw new Error('Collection grid markup not found');

        const productsCount = Number(
          nextGrid.dataset.productsCount || this.dataset.productsCount || 0
        );
        if (updateEvent) updateEvent.resolve({ productsCount });

        if (mode === 'append') {
          this.appendFrom(nextGrid);
          if (updateUrl !== false) {
            this.pushUrl(params);
          }
          this.setLoading(false);
          return;
        }

        if (updateUrl !== false) {
          this.pushUrl(params);
        }
        const imported = document.importNode(nextGrid, true);
        this.replaceWith(imported);
        if (page != null) {
          this.scrollGridIntoView(imported);
        }
      } catch (error) {
        console.error(error);
        if (updateEvent) updateEvent.reject(error);
        this.setLoading(false);
      }
    }

    scrollGridIntoView(grid) {
      if (!grid) return;
      const headerHeight = Number.parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--header-height')
      );
      const offset = Number.isFinite(headerHeight) ? headerHeight : 0;
      const top = grid.getBoundingClientRect().top + window.scrollY - offset;
      const reducedMotion =
        (window.CfonElements && typeof window.CfonElements.prefersReducedMotion === 'function'
          ? window.CfonElements.prefersReducedMotion()
          : false) || window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      window.scrollTo({
        top: Math.max(0, top),
        behavior: reducedMotion ? 'auto' : 'smooth',
      });
    }

    setLoading(isLoading) {
      this.loading = isLoading;
      this.classList.toggle('is-loading', isLoading);
      this.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      const loadingEl = this.querySelector('[data-loading]');
      if (loadingEl) {
        loadingEl.setAttribute('aria-hidden', isLoading ? 'false' : 'true');
      }
    }

    pushUrl(params) {
      const pushParams = new URLSearchParams(params);
      pushParams.delete('section_id');
      const query = pushParams.toString();
      const nextUrl = query ? `${window.location.pathname}?${query}` : window.location.pathname;
      history.pushState({ searchParams: query }, '', nextUrl);
    }

    appendFrom(nextGrid) {
      const list =
        this.querySelector('[data-list]') || this.querySelector('.cfon-product-grid__list');
      const nextList =
        nextGrid.querySelector('[data-list]') || nextGrid.querySelector('.cfon-product-grid__list');
      if (list && nextList) {
        Array.from(nextList.children).forEach((child) => {
          list.appendChild(child);
        });
      }

      this.dataset.currentPage = nextGrid.dataset.currentPage || this.dataset.currentPage;
      this.dataset.totalPages = nextGrid.dataset.totalPages || this.dataset.totalPages;

      const oldPagination = this.querySelector('[data-pagination]');
      const nextPagination = nextGrid.querySelector('[data-pagination]');
      if (oldPagination && nextPagination) {
        oldPagination.replaceWith(nextPagination.cloneNode(true));
        this.unbindPagination();
        this.bindPagination();
      } else if (oldPagination && !nextPagination) {
        oldPagination.remove();
        this.unbindPagination();
      }

      const oldSentinel = this.querySelector('[data-sentinel]');
      if (oldSentinel) oldSentinel.remove();

      const nextSentinel = nextGrid.querySelector('[data-sentinel]');
      if (nextSentinel) {
        this.appendChild(nextSentinel.cloneNode(true));
      }

      this.teardownInfinite();
      this.setupInfinite();
    }
  }

  class CfonArticleGrid extends CfonProductGrid {}

  CfonElements.define('cfon-collection-sort', CfonCollectionSort);
  CfonElements.define('cfon-product-grid', CfonProductGrid);
  CfonElements.define('cfon-article-grid', CfonArticleGrid);
})();
